import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { products } from '../data/products';
import logo from '../assets/logo.jpeg';
import './Home.css';

const Counter = ({ end, suffix = '', label }) => {
  const [count, setCount] = useState(0);
  const ref = useRef();
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          let start = 0;
          const duration = 2000;
          const step = (end / duration) * 16;
          const timer = setInterval(() => {
            start += step;
            if (start >= end) { setCount(end); clearInterval(timer); }
            else setCount(Math.floor(start));
          }, 16);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end]);

  return (
    <div className="stat-item" ref={ref}>
      <div className="stat-number">{count}{suffix}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
};

const Home = () => {
  const heroRef = useRef();
  const [heroLoaded, setHeroLoaded] = useState(false);

  useEffect(() => {
    setTimeout(() => setHeroLoaded(true), 100);
  }, []);

  const featuredProducts = products.slice(0, 6);

  return (
    <div className="home">
      {/* HERO */}
      <section className="hero" ref={heroRef}>
        <div className="hero-bg-pattern" />
        <div className="hero-particles">
          {[...Array(12)].map((_, i) => (
            <div key={i} className="particle" style={{ '--i': i }} />
          ))}
        </div>

        <div className={`hero-content ${heroLoaded ? 'loaded' : ''}`}>
          <div className="hero-badge">
            <img src={logo} alt="SSE" className="hero-badge-logo" />
            <span>Est. 30+ Years of Excellence</span>
          </div>

          <h1 className="hero-title">
            <span className="hero-title-line1">World of</span>
            <span className="hero-title-line2">Rice Mill</span>
            <span className="hero-title-line3">Machinery</span>
          </h1>

          <p className="hero-subtitle">
            Sri Sai Engineerings — Hyderabad's most trusted manufacturer of modern rice mill
            machinery. From paddy to polished rice, we engineer every step.
          </p>

          <div className="hero-actions">
            <Link to="/products" className="hero-btn-primary">
              Explore Products
              <span>→</span>
            </Link>
            <Link to="/contact" className="hero-btn-secondary">
              Get Quote
            </Link>
          </div>

          <div className="hero-scroll-hint">
            <div className="scroll-line" />
            <span>Scroll to discover</span>
          </div>
        </div>

        <div className={`hero-visual ${heroLoaded ? 'loaded' : ''}`}>
          <div className="hero-circle-outer">
            <div className="hero-circle-inner">
              <img src={logo} alt="Sri Sai Engineerings" className="hero-logo-big" />
            </div>
          </div>
          <div className="hero-orbit hero-orbit-1">
            <div className="orbit-dot" />
          </div>
          <div className="hero-orbit hero-orbit-2">
            <div className="orbit-dot" />
          </div>
          <div className="floating-chip chip-1">🌾 Paddy Cleaner</div>
          <div className="floating-chip chip-2">🌾 Pneumatic Husker</div>
          {/* <div className="floating-chip chip-3">✨ Whitener</div> */}
          <div className="floating-chip chip-4">🍚 Silky Polisher</div>
          <div className="floating-chip chip-5">🍚 Rice Shifter</div>
          <div className="floating-chip chip-6">🌾 Paddy Separator</div>
          <div className="floating-chip chip-7">🌾 Pre Cleaner Max</div>
        </div>

        {/* Mobile chips — visible only on mobile, below hero text */}
        <div className="mobile-chips-grid">
          {[
            
  { emoji: '🌀', label: 'Pre Cleaner' },
  { emoji: '🌾', label: 'Paddy Cleaner' },
  { emoji: '🌾', label: 'Pneumatic Husker' },
  { emoji: '🔄', label: 'Paddy Separator' },
  { emoji: '✨', label: 'Whitener' },
  { emoji: '💎', label: 'Silky Polisher' },
  { emoji: '📦', label: 'Rice Shifter' },
  { emoji: '🔒', label: 'Air Lock System' },
  { emoji: '🏗️', label: 'Geared Elevators' },
  { emoji: '🪨', label: 'Color Sortex' }

          ].map((chip, i) => (
            <div key={i} className="mobile-chip-bubble" style={{ animationDelay: `${i * 0.07}s` }}>
              {/* <span className="mcb-emoji">{chip.emoji}</span> */}
              <span className="mcb-label">{chip.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* MOBILE CHIP TICKER - only visible on mobile */}
      <div className="mobile-chip-ticker">
        <div className="ticker-track">
          {["🌾 Paddy Cleaner","⚙️ Pneumatic Husker","✨ Whitener","💎 Silk Polisher","🔄 Paddy Separator","🪨 Destoner","📦 Multi Grader","🌀 Pre Cleaner","🏗️ Geared Elevator","🔒 Air Lock System","🌾 Paddy Cleaner","⚙️ Pneumatic Husker","✨ Whitener","💎 Silk Polisher","🔄 Paddy Separator","🪨 Destoner"].map((chip, i) => (
            <span key={i} className="ticker-chip">{chip}</span>
          ))}
        </div>
      </div>

      {/* STATS */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            <Counter end={30} suffix="+" label="Years of Experience" />
            <Counter end={15} suffix="+" label="Product Types" />
            <Counter end={500} suffix="+" label="Mills Served" />
            <Counter end={100} suffix="%" label="Client Satisfaction" />
          </div>
        </div>
      </section>

      {/* ABOUT STRIP */}
      <section className="about-strip">
        <div className="container">
          <div className="about-strip-inner">
            <div className="about-strip-text">
              <span className="section-label">Who We Are</span>
              <h2 className="section-title">Modern Rice Mill <span style={{ color: 'var(--green-bright)' }}>Pioneers</span></h2>
              <p>
                For more than three decades, Sri Sai Engineerings has been at the forefront of manufacturing
                superior quality Modern Rice Mill Machinery. Our designs are developed in our R&D department
                under the supervision of experienced, efficient technical staff to ensure high quality output.
              </p>
              <p>
                We have brought modern technology in the field of polishing, shelling, grading, and also
                undertake complete Engineering Consultancy and provide Services on Turkey basis for Rice Millers.
              </p>
              <Link to="/about" className="learn-more-btn">Learn More About Us →</Link>
            </div>
            <div className="about-strip-cards">
              {[
                { icon: '🏭', title: 'State-of-Art Facility', desc: 'Up-to-date computerized designs and engineering experts.' },
                { icon: '🌏', title: 'Extensive Markets', desc: 'Extensive infrastructure continuously improving quality.' },
                { icon: '🔧', title: 'Turkey Key Service', desc: 'Complete engineering consultancy for rice millers.' },
                { icon: '📐', title: 'R&D Department', desc: 'In-house research to develop cutting-edge machinery.' },
              ].map((card, i) => (
                <div className="about-card" key={i}>
                  <div className="about-card-icon">{card.icon}</div>
                  <div>
                    <h4>{card.title}</h4>
                    <p>{card.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCTS PREVIEW */}
      <section className="products-preview">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Our Range</span>
            <h2 className="section-title">Featured <span style={{ color: 'var(--green-bright)' }}>Products</span></h2>
            <p className="section-subtitle">
              A complete ecosystem of rice milling machinery — from raw paddy to market-ready polished rice.
            </p>
          </div>

          <div className="products-grid-home">
            {featuredProducts.map((product, i) => (
              <Link to={`/products/${product.id}`} key={product.id} className="product-home-card" style={{ '--color': product.color, animationDelay: `${i * 0.08}s` }}>
                <div className="phc-image">
                  {product.image ? (
                    <img src={product.image} alt={product.name} className="phc-img" />
                  ) : (
                    <div className="phc-image-placeholder">
                      <span className="phc-icon">⚙️</span>
                    </div>
                  )}
                  <div className="phc-overlay">
                    <span className="phc-view">View Details →</span>
                  </div>
                  <div className="phc-badge">{product.category}</div>
                </div>
                <div className="phc-body">
                  <div className="phc-accent" style={{ background: product.color }} />
                  <h3 className="phc-name">{product.name}</h3>
                  <p className="phc-tagline">{product.tagline}</p>
                  <div className="phc-capacity">
                    <span>Capacity: 2-4 Ton/Hr</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="products-view-all">
            <Link to="/products" className="view-all-btn">
              View All {products.length} Products →
            </Link>
          </div>
        </div>
      </section>

      {/* PROCESS SECTION */}
      {/* <section className="process-section">
        <div className="process-bg" />
        <div className="container"> */}
          {/* <div className="section-header light">
            <span className="section-label" style={{ color: 'var(--green-pale)' }}>How It Works</span>
            <h2 className="section-title" style={{ color: '#fff' }}>The Milling <span style={{ color: 'var(--gold)' }}>Journey</span></h2>
          </div> */}
{/*           
          <div className="process-steps">
            {[
              { step: '01', label: 'Pre Cleaning', desc: 'Remove stones, straw & heavy impurities', icon: '🌾' },
              { step: '02', label: 'De-husking', desc: 'Pneumatic rubber shellers remove husk efficiently', icon: '⚙️' },
              { step: '03', label: 'Separation', desc: 'Separate brown rice from paddy using gravity', icon: '🔄' },
              { step: '04', label: 'Whitening', desc: 'Remove bran layer with abrasive rollers', icon: '✨' },
              { step: '05', label: 'Polishing', desc: 'Create glossy, silky surface finish on rice', icon: '💎' },
              { step: '06', label: 'Grading', desc: 'Sort by size — head rice, tips, broken', icon: '📦' },
            ].map((step, i) => (
              <div className="process-step" key={i}>
                <div className="process-step-num">{step.step}</div>
                <div className="process-step-icon">{step.icon}</div>
                <h4 className="process-step-label">{step.label}</h4>
                <p className="process-step-desc">{step.desc}</p>
                {i < 5 && <div className="process-connector" />}
              </div>
            ))}
          </div>
        </div>
      </section> */}
      {/* PROCESS SECTION */}
{/* PROCESS SECTION */}
<section className="process-section">
  <div className="process-bg" />
  <div className="container">

    <div className="process-header">
      <span className="process-eyebrow">How It Works</span>
      <h2 className="process-title">
        The Milling <span>Journey</span>
      </h2>
      <p className="process-subtitle">
        Six precision stages — from raw paddy to market-ready polished rice.
      </p>
    </div>

    <div className="process-steps">
      {[
        {
          step: '01', label: 'Pre Cleaning', desc: 'Remove stones, straw & heavy impurities before milling begins.',
          icon: <svg width="28" height="28" viewBox="0 0 28 28" fill="none"><rect x="3" y="6" width="22" height="2.5" rx="1.25" fill="#81C784"/><rect x="6" y="12" width="16" height="2.5" rx="1.25" fill="#81C784" opacity=".75"/><rect x="10" y="18" width="8" height="2.5" rx="1.25" fill="#81C784" opacity=".5"/><circle cx="22" cy="22" r="3.5" stroke="#4CAF50" strokeWidth="1.5"/><line x1="22" y1="20" x2="22" y2="24" stroke="#4CAF50" strokeWidth="1.5" strokeLinecap="round"/><line x1="20" y1="22" x2="24" y2="22" stroke="#4CAF50" strokeWidth="1.5" strokeLinecap="round"/></svg>
        },
        {
          step: '02', label: 'De-husking', desc: 'Pneumatic rubber rollers crack and remove the outer husk cleanly.',
          icon: <svg width="28" height="28" viewBox="0 0 28 28" fill="none"><circle cx="9" cy="14" r="6" stroke="#4CAF50" strokeWidth="1.5"/><circle cx="19" cy="14" r="6" stroke="#4CAF50" strokeWidth="1.5"/><path d="M14 8 Q12 14 14 20" stroke="#81C784" strokeWidth="1.5" strokeLinecap="round"/><ellipse cx="14" cy="14" rx="2" ry="4" fill="#81C784" opacity=".4"/></svg>
        },
        {
          step: '03', label: 'Separation', desc: 'Gravity tables separate brown rice from un-husked paddy.',
          icon: <svg width="28" height="28" viewBox="0 0 28 28" fill="none"><path d="M4 8 L14 4 L24 8" stroke="#4CAF50" strokeWidth="1.5" strokeLinecap="round"/><path d="M4 14 L14 10 L24 14" stroke="#4CAF50" strokeWidth="1.5" strokeLinecap="round" opacity=".7"/><path d="M4 20 L14 16 L24 20" stroke="#4CAF50" strokeWidth="1.5" strokeLinecap="round" opacity=".4"/><circle cx="7" cy="23" r="2.5" fill="#81C784"/><circle cx="21" cy="9" r="2" stroke="#81C784" strokeWidth="1.5"/></svg>
        },
        {
          step: '04', label: 'Whitening', desc: 'Abrasive rollers strip the bran layer to achieve white rice.',
          icon: <svg width="28" height="28" viewBox="0 0 28 28" fill="none"><rect x="11" y="4" width="6" height="20" rx="3" stroke="#4CAF50" strokeWidth="1.5"/><path d="M7 9 Q4 14 7 19" stroke="#81C784" strokeWidth="1.5" strokeLinecap="round"/><path d="M21 9 Q24 14 21 19" stroke="#81C784" strokeWidth="1.5" strokeLinecap="round"/><circle cx="14" cy="14" r="2.5" fill="#4CAF50" opacity=".5"/></svg>
        },
        {
          step: '05', label: 'Polishing', desc: 'Water-mist polishers create the final silky, glossy finish.',
          icon: <svg width="28" height="28" viewBox="0 0 28 28" fill="none"><path d="M14 3 L16.5 10.5 L24 10.5 L18 15.5 L20.5 23 L14 18 L7.5 23 L10 15.5 L4 10.5 L11.5 10.5 Z" stroke="#4CAF50" strokeWidth="1.5" strokeLinejoin="round" fill="none"/><circle cx="14" cy="14" r="2.5" fill="#81C784" opacity=".6"/></svg>
        },
        {
          step: '06', label: 'Grading', desc: 'Multi-deck graders sort by size — head rice, brokens & tips.',
          icon: <svg width="28" height="28" viewBox="0 0 28 28" fill="none"><circle cx="7" cy="20" r="4.5" stroke="#4CAF50" strokeWidth="1.5"/><circle cx="17" cy="16" r="3.5" stroke="#4CAF50" strokeWidth="1.5" opacity=".8"/><circle cx="24" cy="11" r="2.5" stroke="#4CAF50" strokeWidth="1.5" opacity=".55"/><line x1="3" y1="25" x2="27" y2="7" stroke="#81C784" strokeWidth="1" strokeDasharray="2 2" opacity=".35"/></svg>
        },
      ].map((s, i) => (
        <div className="process-step" key={i}>
          {i < 5 && <div className="process-arrow">›</div>}
          <div className="pstep-icon">{s.icon}</div>
          <div className="pstep-num">{s.step}</div>
          <h4 className="pstep-label">{s.label}</h4>
          <p className="pstep-desc">{s.desc}</p>
        </div>
      ))}
    </div>

  </div>
</section>

      {/* WHY CHOOSE US */}
      <section className="why-section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Why Us</span>
            <h2 className="section-title">Built for <span style={{ color: 'var(--green-bright)' }}>Performance</span></h2>
          </div>
          <div className="why-grid">
            {[
              { icon: '🏆', title: '30+ Years Experience', desc: 'Decades of engineering expertise in rice mill machinery manufacturing.' },
              { icon: '🔬', title: 'R&D Driven', desc: 'In-house research and development for continuous improvement.' },
              { icon: '⚡', title: 'High Efficiency', desc: 'Machines engineered for maximum output with minimum power consumption.' },
              { icon: '🛠️', title: 'Turkey Key Projects', desc: 'Complete turnkey solutions from planning to commissioning.' },
              { icon: '🌿', title: 'Eco-Conscious', desc: 'Efficient bran separation systems reduce waste and improve yield.' },
              { icon: '📞', title: 'Dedicated Support', desc: 'Technical support team always available for service and maintenance.' },
            ].map((item, i) => (
              <div className="why-card" key={i}>
                <div className="why-icon">{item.icon}</div>
                <h4 className="why-title">{item.title}</h4>
                <p className="why-desc">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-inner">
            <div className="cta-left">
              <span className="cta-eyebrow">Turnkey Solutions</span>
              <h2 className="cta-heading">
                From Paddy to<br />
                <span>Polished Rice</span> —<br />
                We Engineer It All.
              </h2>
              <p className="cta-subtext">
                Whether you're setting up a new mill or upgrading an existing one, Sri Sai Engineerings
                delivers complete solutions — machinery, installation, and technical support.
              </p>
              <div className="cta-actions">
                <Link to="/contact" className="cta-btn-primary">Start Your Project →</Link>
                <Link to="/products" className="cta-btn-ghost">Browse Machinery</Link>
              </div>
            </div>

            <div className="cta-right">
              <div className="cta-contact-card">
                <div className="ccc-label">Reach Us Directly</div>
                <a href="tel:+919848027990" className="ccc-phone">
                  <span className="ccc-phone-icon">📞</span>
                  <div>
                    <div className="ccc-phone-num">9848027990</div>
                    <div className="ccc-phone-sub">Mon–Sat, 9 AM – 7 PM IST</div>
                  </div>
                </a>
                <a href="mailto:srisaiengs@gmail.com" className="ccc-email">
                  ✉️ srisaiengs@gmail.com
                </a>
                <div className="ccc-divider" />
                <div className="ccc-features">
                  {['Free technical consultation', 'Turnkey project delivery', 'Post-installation support'].map(f => (
                    <div key={f} className="ccc-feature">
                      <span className="ccc-check">✓</span> {f}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;